import React from "react";

export default function MachineUtilisationSection() {
  const checklist = [
    "Identify idle machines",
    "Improve machine utilisation",
    "Plan production capacity",
    "Avoid overbooking",
    "Allocate jobs efficiently",
    "Track machine performance",
  ];

  const stats = [
    { value: "78%", label: "MACHINE UTILISATION" },
    { value: "22%", label: "AVAILABLE CAPACITY" },
    { value: "42", label: "ACTIVE MACHINES" },
    { value: "6", label: "IDLE MACHINES" },
    { value: "120 hrs", label: "UPCOMING CAPACITY" },
  ];

  const legend = [
    { color: "#22c55e", label: "Available" },
    { color: "#3b82f6", label: "Booked" },
    { color: "#f7941d", label: "In Production" },
    { color: "#ef7f0e", label: "Under Maintenance" },
    { color: "#94a3c4", label: "Idle" },
    { color: "#ef4444", label: "Unavailable" },
  ];

  return (
    <>
      <ResponsiveStyles />
      <section className="mu-section" style={styles.section}>
        <div className="mu-grid-overlay" style={styles.gridOverlay} />
        <div className="mu-wrap" style={styles.wrap}>
          {/* LEFT */}
          <div className="mu-left">
            <span className="mu-eyebrow" style={styles.eyebrow}>
              <span style={styles.eyebrowDot} />
              MACHINE UTILISATION
            </span>

            <h2 className="mu-heading" style={styles.heading}>
              Turn idle machine
              <br />
              capacity into <span style={styles.accent}>new</span>
              <br />
              <span style={styles.accent}>business.</span>
            </h2>

            <p className="mu-lede" style={styles.lede}>
              Every unused machine hour is a missed opportunity. Understand
              utilisation, identify available capacity and plan production
              around what your facility can actually deliver.
            </p>

            <ul className="mu-checklist" style={styles.checklist}>
              {checklist.map((item, i) => (
                <li className="mu-check-item" style={styles.checkItem} key={i}>
                  <span className="mu-check-icon" style={styles.checkIcon}>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#f7941d"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="9" />
                      <path d="M8.5 12.2l2.2 2.2 4.8-4.9" />
                    </svg>
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* RIGHT */}
          <div className="mu-panel" style={styles.panel}>
            <div className="mu-stats-row" style={styles.statsRow}>
              {stats.map((s, i) => (
                <div className="mu-stat-card" style={styles.statCard} key={i}>
                  <div className="mu-stat-value" style={styles.statValue}>
                    {s.value}
                  </div>
                  <div className="mu-stat-label" style={styles.statLabel}>
                    {s.label}
                  </div>
                </div>
              ))}
            </div>

            <div className="mu-capacity-row" style={styles.capacityRow}>
              <span className="mu-capacity-label" style={styles.capacityLabel}>
                Overall Capacity
              </span>
              <span className="mu-capacity-value" style={styles.capacityValueText}>
                78% utilised
              </span>
            </div>
            <div className="mu-progress-track" style={styles.progressTrack}>
              <div className="mu-progress-fill" style={styles.progressFill} />
            </div>

            <div className="mu-legend-grid" style={styles.legendGrid}>
              {legend.map((l, i) => (
                <div className="mu-legend-chip" style={styles.legendChip} key={i}>
                  <span
                    className="mu-legend-dot"
                    style={{ ...styles.legendDot, background: l.color }}
                  />
                  <span className="mu-legend-text" style={styles.legendText}>
                    {l.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

/**
 * Responsive rules. Mobile switches every grid (stats row, legend grid,
 * and the main two-column layout) to a flex column so nothing depends on
 * squeezing into fixed column tracks on narrow screens.
 */
function ResponsiveStyles() {
  return (
    <style>{`
      @media (max-width: 1024px) {
        .mu-wrap {
          display: flex !important;
          flex-direction: column !important;
          grid-template-columns: none !important;
          gap: 44px !important;
        }
        .mu-left, .mu-panel {
          width: 100% !important;
          max-width: 100% !important;
          box-sizing: border-box !important;
        }
        .mu-stats-row {
          grid-template-columns: repeat(3, 1fr) !important;
        }
      }

      @media (max-width: 640px) {
        .mu-section {
          padding: 48px 0 !important;
          min-height: auto !important;
        }
        .mu-wrap {
          padding: 0 18px !important;
          gap: 36px !important;
        }
        .mu-eyebrow {
          font-size: 10.5px !important;
          padding: 7px 14px !important;
        }
        .mu-heading {
          font-size: clamp(1.7rem, 8vw, 2.3rem) !important;
          margin-top: 20px !important;
        }
        .mu-lede {
          font-size: 0.96rem !important;
          margin-top: 18px !important;
        }
        .mu-checklist {
          margin-top: 24px !important;
        }
        .mu-check-item {
          font-size: 0.92rem !important;
        }
        .mu-panel {
          padding: 20px !important;
          border-radius: 18px !important;
        }
        .mu-stats-row {
          display: flex !important;
          flex-direction: column !important;
          grid-template-columns: none !important;
          gap: 10px !important;
        }
        .mu-stat-card {
          width: 100% !important;
          box-sizing: border-box !important;
          display: flex !important;
          align-items: baseline !important;
          justify-content: space-between !important;
          padding: 14px 16px !important;
        }
        .mu-stat-value {
          font-size: 1.3rem !important;
          margin-bottom: 0 !important;
        }
        .mu-stat-label {
          font-size: 0.68rem !important;
        }
        .mu-capacity-row {
          margin-top: 20px !important;
          font-size: 0.85rem !important;
        }
        .mu-legend-grid {
          grid-template-columns: 1fr !important;
          margin-top: 20px !important;
          gap: 8px !important;
        }
        .mu-legend-chip {
          padding: 12px 14px !important;
        }
      }

      @media (max-width: 380px) {
        .mu-heading {
          font-size: 1.5rem !important;
        }
      }
    `}</style>
  );
}

const styles = {
  section: {
    position: "relative",
    width: "100%",
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    background: "linear-gradient(180deg, #050914 0%, #080d24 100%)",
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    boxSizing: "border-box",
    padding: "72px 0",
    overflow: "hidden",
    color: "#ffffff",
  },
  gridOverlay: {
    position: "absolute",
    inset: 0,
    backgroundImage:
      "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
    backgroundSize: "44px 44px",
    pointerEvents: "none",
  },
  wrap: {
    position: "relative",
    zIndex: 2,
    width: "100%",
    maxWidth: 1320,
    margin: "0 auto",
    padding: "0 32px",
    display: "grid",
    gridTemplateColumns: "0.85fr 1.15fr",
    gap: 56,
    alignItems: "center",
    boxSizing: "border-box",
  },
  eyebrow: {
    display: "inline-flex",
    alignItems: "center",
    gap: 9,
    padding: "9px 18px",
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(255,255,255,0.04)",
    fontFamily: "'Sora', 'Segoe UI', sans-serif",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.12em",
    color: "#e7ebff",
  },
  eyebrowDot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: "#f7941d",
    flexShrink: 0,
  },
  heading: {
    fontFamily: "'Sora', 'Segoe UI', sans-serif",
    fontWeight: 800,
    fontSize: "clamp(2rem, 3.8vw, 3rem)",
    lineHeight: 1.12,
    letterSpacing: "-0.02em",
    color: "#ffffff",
    marginTop: 24,
  },
  accent: {
    color: "#f7941d",
  },
  lede: {
    marginTop: 24,
    maxWidth: 520,
    fontSize: "1.03rem",
    lineHeight: 1.75,
    color: "#aab2d1",
  },
  checklist: {
    marginTop: 28,
    listStyle: "none",
    padding: 0,
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  checkItem: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    fontSize: "1rem",
    color: "#dfe3f5",
  },
  checkIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  panel: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 22,
    padding: "28px",
    boxSizing: "border-box",
  },
  statsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: 12,
  },
  statCard: {
    background: "rgba(30,45,90,0.55)",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 12,
    padding: "16px 14px",
    boxSizing: "border-box",
  },
  statValue: {
    fontFamily: "'Sora', 'Segoe UI', sans-serif",
    fontWeight: 800,
    fontSize: "1.6rem",
    color: "#ffffff",
    marginBottom: 6,
  },
  statLabel: {
    fontSize: "0.68rem",
    fontWeight: 600,
    letterSpacing: "0.05em",
    color: "#9aa2c4",
    lineHeight: 1.4,
  },
  capacityRow: {
    marginTop: 28,
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    fontSize: "0.95rem",
  },
  capacityLabel: {
    color: "#c7cce3",
  },
  capacityValueText: {
    color: "#aab2d1",
  },
  progressTrack: {
    marginTop: 10,
    width: "100%",
    height: 10,
    borderRadius: 999,
    background: "rgba(255,255,255,0.08)",
    overflow: "hidden",
  },
  progressFill: {
    width: "78%",
    height: "100%",
    borderRadius: 999,
    background: "linear-gradient(90deg, #f7941d, #ffb45c)",
  },
  legendGrid: {
    marginTop: 24,
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 12,
  },
  legendChip: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    background: "rgba(30,45,90,0.4)",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 10,
    padding: "12px 16px",
    boxSizing: "border-box",
  },
  legendDot: {
    width: 9,
    height: 9,
    borderRadius: "50%",
    flexShrink: 0,
  },
  legendText: {
    fontSize: "0.92rem",
    color: "#dfe3f5",
  },
};